import { Box, Chip, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import type { ReactNode } from 'react';
import { Model } from '../models';
import AppleIcon from './icons/AppleIcon';
import ManualIcon from './icons/ManualIcon';
import StripeIcon from './icons/StripeIcon';

type Props = {
  status: Model.Subscription.Status;
  source?: Model.Subscription.Source;
  endsAt?: string | null;
  retryUntil?: string | null;
  isIndividual?: boolean;
};

const STATUS_LABEL: Record<Model.Subscription.Status, string> = {
  pending: 'Pending',
  active: 'Active',
  active_ending: 'Active (ending)',
  inactive: 'Inactive',
  payment_overdue: 'Payment overdue',
  payment_failed: 'Payment failed',
  licence_revoked: 'Licence revoked',
};

const STATUS_COLOR: Record<
  Model.Subscription.Status,
  'success' | 'error' | 'warning' | 'info' | 'default'
> = {
  pending: 'success',
  active: 'success',
  active_ending: 'success',
  inactive: 'error',
  payment_overdue: 'warning',
  payment_failed: 'error',
  licence_revoked: 'warning',
};

const SOURCE_LABEL: Record<Model.Subscription.Source, string> = {
  stripe: 'via Stripe',
  in_app: 'Via IAP',
  manual: 'Manual Sub.',
  b2b: 'B2B',
};

function SourceBadge({ source }: { source: Model.Subscription.Source }) {
  let icon: ReactNode;
  switch (source) {
    case 'stripe':
      icon = <StripeIcon />;
      break;
    case 'in_app':
      icon = <AppleIcon />;
      break;
    case 'manual':
      icon = <ManualIcon />;
      break;
    case 'b2b':
      return null;
  }
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        color: 'text.secondary',
      }}
    >
      {icon}
      <Typography variant="body2" component="span">
        {SOURCE_LABEL[source]}
      </Typography>
    </Box>
  );
}

export default function SubscriptionDisplay({
  status,
  source,
  endsAt,
  retryUntil,
  isIndividual,
}: Props) {
  let subtitle: ReactNode = null;
  if (status === 'inactive' && isIndividual) {
    subtitle = <Typography variant="body1">Individual</Typography>;
  } else if (
    endsAt &&
    (status === 'active' ||
      status === 'active_ending' ||
      status === 'licence_revoked')
  ) {
    subtitle = (
      <Typography variant="body1">
        Ends {format(parseISO(endsAt), 'd MMM yyyy')}
      </Typography>
    );
  } else if (status === 'payment_overdue' && retryUntil) {
    subtitle = (
      <Typography variant="body1">
        We will retry the payment until {format(parseISO(retryUntil), 'd. MMM')}
        .
      </Typography>
    );
  } else if (status === 'payment_failed') {
    subtitle = (
      <Typography variant="body1" sx={{ color: 'secondary.main' }}>
        The subscription has been cancelled due to failed payment.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Chip
          label={STATUS_LABEL[status]}
          size="small"
          color={STATUS_COLOR[status]}
          sx={{ borderRadius: 1 }}
        />
        {source && <SourceBadge source={source} />}
      </Stack>
      {subtitle}
    </Stack>
  );
}
